import { createContext, useContext, useEffect, useState } from "react";
import api from "../api";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("hin_token"));
  const [loading, setLoading] = useState(true);

  // fetch current user if token exists
  useEffect(() => {
    const init = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const res = await api.get("/auth/me");
        console.log("[AuthContext] /auth/me returned:", res.data);
        const u = res.data || {};
        // normalize user shape: prefer username from top-level or profile
        const displayName =
          u.username || (u.profile && u.profile.username) || null;
        const photoURL =
          (u.profile && u.profile.photoURL) || u.photoURL || null;
        setUser({ ...u, displayName, photoURL });
      } catch (err) {
        console.error("Failed to fetch current user", err);
        localStorage.removeItem("hin_token");
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [token]);

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    const t = res.data.token;
    if (t) {
      localStorage.setItem("hin_token", t);
      setToken(t);
      // fetch user
      const me = await api.get("/auth/me");
      const u = me.data || {};
      const displayName =
        u.username || (u.profile && u.profile.username) || null;
      const photoURL = (u.profile && u.profile.photoURL) || u.photoURL || null;
      setUser({ ...u, displayName, photoURL });
    }
    return res.data;
  };

  // Login using Google ID token (from Google Identity Services)
  const loginWithGoogle = async (idToken) => {
    // idToken is the JWT issued by Google (frontend obtains it via GIS)
    const res = await api.post("/auth/google", { id_token: idToken });
    const t = res.data.token;
    if (t) {
      localStorage.setItem("hin_token", t);
      setToken(t);
      const me = await api.get("/auth/me");
      const u = me.data || {};
      const displayName =
        u.username || (u.profile && u.profile.username) || null;
      const photoURL = (u.profile && u.profile.photoURL) || u.photoURL || null;
      setUser({ ...u, displayName, photoURL });
    }
    return res.data;
  };

  // loginWithGoogle removed: Google OAuth has been disabled. Use regular login/signup flows.

  const signup = async (email, password, extra = {}) => {
    // extra can include username, uid (from otp), etc.
    const res = await api.post("/auth/signup", { email, password, ...extra });
    const t = res.data.token;
    if (t) {
      localStorage.setItem("hin_token", t);
      setToken(t);
      const me = await api.get("/auth/me");
      const u = me.data || {};
      const displayName =
        u.username || (u.profile && u.profile.username) || null;
      const photoURL = (u.profile && u.profile.photoURL) || u.photoURL || null;
      setUser({ ...u, displayName, photoURL });
    }
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem("hin_token");
    setToken(null);
    setUser(null);
  };

  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated,
        login,
        signup,
        logout,
        loginWithGoogle,
      }}
    >
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
